import type { FC } from 'react';
import { useState } from 'react';
import { Grid, Box, Button } from '@mui/material';
import WarningIcon from '@mui/icons-material/Warning';
import CancelIcon from '@mui/icons-material/Cancel';
import { useNavigate, useParams } from 'react-router-dom';
import orderApi from 'src/api/OrderApi';
import useOrder from 'src/hooks/useOrder';
import useOrderSteps from 'src/hooks/useOrderSteps';
import useSnackbar from 'src/hooks/useSnackbar';
import useCustomer from 'src/hooks/useCustomer';
import ConfirmationDialog from 'src/components/controls/ConfirmationDialog';
import ArrowLeftIcon from 'src/icons/ArrowLeft';

interface OrderEntryTopNavProps {
  buttonList?: ('delete' | 'back' | 'save')[];
  backPath?: string;
  backButtonText?: string;
}

const OrderEntryTopNav: FC<React.PropsWithChildren<OrderEntryTopNavProps>> = ({ buttonList = ['save', 'delete'], backPath, backButtonText = 'Back to Customer' }) => {
  const { sfaOrderId } = useParams();
  const [confirmOpen, setConfirmOpen] = useState<boolean>(false);
  const currentOrderStep = useOrderSteps((state) => state.currentOrderStep);
  const saveOrderHeader = useOrder((state) => state.saveOrderHeader);
  const orderIsOpenOrderEdit = useOrder((state) => state.order.isOpenOrderEdit);
  const customerNumber = useCustomer((state) => state.customer.number);
  const { openSnackBar } = useSnackbar();
  let navigate = useNavigate();
  const navigationPath = backPath || `/${customerNumber}`;

  const deleteOrder = (): void => {
    console.log('Deleting order:', sfaOrderId);
    orderApi
      .deleteUnsubmittedOrder(Number(sfaOrderId))
      .then(() => {
        if (orderIsOpenOrderEdit) {
          openSnackBar('Changes canceled.', 'info', 2000);
          navigate(navigationPath);
        } else {
          openSnackBar('Order deleted.', 'info', 2000);
          navigate(navigationPath);
        }
      })
      .catch((err) => {
        console.error(err);
        openSnackBar('Order delete failed.' + err, 'error', 2000);
      });
  };

  const handleSaveOrderHeader = async () => {
    if (currentOrderStep === 3) {
      const isSaved = await saveOrderHeader();
      if (!isSaved) {
        console.error('Failed to save order header');
        return;
      }
    }
    navigate(`/${customerNumber}`);
  };

  return (
    <>
      {buttonList.includes('delete') && (
        <ConfirmationDialog
          title={orderIsOpenOrderEdit ? 'Cancel Changes?' : 'Delete Order?'}
          titleIconStart={<WarningIcon />}
          cancelText="No"
          okText="Yes"
          maxWidth="sm"
          open={confirmOpen}
          onClose={(confirmed) => {
            setConfirmOpen(false);
            if (!confirmed) {
              return;
            }

            deleteOrder();
          }}
        >
          <Box sx={{ mb: 2 }}>
            {orderIsOpenOrderEdit ? (
              'Are you sure you want to cancel your changes?'
            ) : (
              <span>
                Are you sure you want to delete this order (<strong>{sfaOrderId}</strong>) ?
              </span>
            )}
          </Box>
        </ConfirmationDialog>
      )}

      <Grid id="contentHeader" container alignItems="end" justifyContent="space-between" width="auto" spacing={2} size={{ xs: 12 }}>
        <Box className="content-header" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          {buttonList.includes('save') && (
            <>
              {orderIsOpenOrderEdit ? (
                <Button variant="contained" onClick={() => navigate(`/${customerNumber}`)} size="small" className="back-btn">
                  <ArrowLeftIcon></ArrowLeftIcon>
                  <div className="back-btn__text-container">
                    <span className="back-btn__text">Back to Customer</span>
                  </div>
                </Button>
              ) : (
                <Button variant="contained" onClick={handleSaveOrderHeader} size="small" className="back-btn">
                  <ArrowLeftIcon></ArrowLeftIcon>
                  <div className="back-btn__text-container">
                    <span className="back-btn__text">Save and Exit</span>
                  </div>
                </Button>
              )}
            </>
          )}
          {buttonList.includes('back') && (
            <Button variant="contained" onClick={() => navigate(navigationPath)} size="small" className="back-btn" sx={{ minWidth: '142px' }}>
              <ArrowLeftIcon></ArrowLeftIcon>
              <div className="back-btn__text-container">
                <span className="back-btn__text">{backButtonText}</span>
              </div>
            </Button>
          )}
          {buttonList.includes('delete') && (
            <>
              <Box />
              <Button variant="contained" onClick={() => setConfirmOpen(true)} size="small" className="back-btn back-btn-delete">
                <CancelIcon></CancelIcon>
                <div className="back-btn__text-container">
                  <span className="back-btn__text">{orderIsOpenOrderEdit ? <>Cancel Changes</> : <>Delete Order</>} </span>
                </div>
              </Button>
            </>
          )}
        </Box>
      </Grid>
    </>
  );
};

export default OrderEntryTopNav;
